// src/pages/Login/LoginPage.tsx

import {
  EmailTwoTone,
  VisibilityOffTwoTone,
  VisibilityTwoTone,
  VpnKeyTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  Slide,
  TextField,
} from '@mui/material'
import { ComponentProps, forwardRef, ReactElement, Ref, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  getStorage,
  isValidEmail,
  isValidLength,
  isValidPassword,
  notifySuccess,
} from '../../api'
import { KTextField } from '../../components'
import { languageConstants as LC } from '../../constants'
import { useOperatorApi, useUserApi } from '../../hooks'

const { USER_PASSWORD_CHANGED } = LC

const Transition = forwardRef(
  (
    props: {
      children: ReactElement
    } & ComponentProps<typeof Slide>,
    ref: Ref<unknown>
  ) => <Slide ref={ref} {...props} />
)

Transition.displayName = 'Transition'

const onDoNothing = event => event.preventDefault()

export const LoginPage = () => {
  const { resetPassword, sendResetCode, sendSignup } = useUserApi()
  const { login } = useOperatorApi()

  /* states */

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [openModal, setOpenModal] = useState(false)

  const [showModalReset1, setShowModalReset1] = useState<boolean>(true)
  const [showModalReset2, setShowModalReset2] = useState<boolean>(false)

  /* functions */

  const onSubmit = event => {
    event.preventDefault()

    login(email, password, false)
  }

  const closeModal = () => {
    setOpenModal(false)
    setShowModalReset1(true)
    setShowModalReset2(false)
  }

  const onChangeConfirmationCode = event =>
    setVerificationCode(event.target.value)
  const onChangeEmail = event => setEmail(event.target.value.toLowerCase())
  const onChangeNewPassword = event => setNewPassword(event.target.value)
  const onChangePassword = event => setPassword(event.target.value)
  const onCodeRequest = () => {
    sendResetCode(email)

    setShowModalReset1(false)
    setShowModalReset2(true)
  }
  const onOpenModal = () => setOpenModal(true)
  const onResetPassword = () => {
    resetPassword(email, newPassword, verificationCode)

    closeModal()
    notifySuccess(USER_PASSWORD_CHANGED)
  }
  const onSendConfirmation = () => sendSignup(email)
  const onTogglePasswordMode = () => setShowPassword(!showPassword)

  /* constants */

  const CODE_REQUEST_DISABLED = !isValidEmail(email) || email === ''

  const RESET_PASSWORD_DISABLED =
    !isValidEmail(email) ||
    email === '' ||
    !isValidLength(verificationCode, 6) ||
    verificationCode === '' ||
    !isValidPassword(newPassword) ||
    newPassword === ''

  const SEND_CONFIRMATION_DISABLED = !isValidEmail(email)

  const LOGIN_BUTTON_DISABLED =
    !isValidEmail(email) || !isValidPassword(password)

  /* component */

  return (
    <>
      {/* password reset */}
      <Dialog
        keepMounted
        onClose={closeModal}
        open={openModal}
        slots={{ transition: Transition }}
        slotProps={{ transition: { direction: 'down' } }}
      >
        <DialogTitle>Passwort vergessen?</DialogTitle>

        <DialogContent>
          {showModalReset1 && (
            <Alert severity={'info'}>
              Bitte geben Sie die E-Mail Adresse von Ihrem Karma Konto an. Wir
              schicken Ihnen einen Code zum Zurücksetzen des Passwortes an diese
              Adresse. Bitte überprüfen Sie den Erhalt ggfs. auch in Ihrem Spam
              Ordner.
            </Alert>
          )}

          <KTextField
            autoComplete={'email'}
            error={email !== '' && !isValidEmail(email)}
            icon={<EmailTwoTone />}
            label={'E-Mail'}
            onChange={onChangeEmail}
            type={'email'}
            value={email}
          />

          {showModalReset2 && (
            <>
              <KTextField
                error={
                  verificationCode !== '' && !isValidLength(verificationCode, 6)
                }
                icon={<VpnKeyTwoTone />}
                label={'Verifizierungscode'}
                onChange={onChangeConfirmationCode}
                value={verificationCode}
              />

              <FormControl fullWidth>
                <TextField
                  autoComplete={'new-password'}
                  error={newPassword !== '' && !isValidPassword(newPassword)}
                  label={'Neues Passwort'}
                  helperText={
                    newPassword !== '' &&
                    !isValidPassword(newPassword) &&
                    'Passwort muss mindestens 8 Zeichen lang sein, 1 Ziffer, Groß- und Kleinbuchstaben enthalten.'
                  }
                  onChange={onChangeNewPassword}
                  value={newPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position={'end'}>
                        <IconButton
                          tabIndex={-1}
                          onClick={onTogglePasswordMode}
                          onMouseDown={onDoNothing}
                        >
                          {showPassword ? (
                            <VisibilityTwoTone />
                          ) : (
                            <VisibilityOffTwoTone />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  type={showPassword ? 'text' : 'password'}
                />
              </FormControl>
            </>
          )}
        </DialogContent>

        <DialogActions>
          {showModalReset1 && (
            <Button disabled={CODE_REQUEST_DISABLED} onClick={onCodeRequest}>
              Code anfordern
            </Button>
          )}

          {showModalReset2 && (
            <Button
              disabled={RESET_PASSWORD_DISABLED}
              onClick={onResetPassword}
            >
              Zurücksetzen
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={true} sx={{ m: 'auto', zIndex: 4 }}>
        <form name={'form'} onSubmit={onSubmit}>
          <DialogTitle>Willkommen bei KARMA</DialogTitle>

          <DialogContent>
            <FormControl sx={{ mt: 2 }} fullWidth>
              <TextField
                autoComplete={'email'}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position={'end'}>
                        <IconButton tabIndex={-1}>
                          <EmailTwoTone />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                label={'E-Mail'}
                onChange={onChangeEmail}
                type={'email'}
                value={email}
              />
            </FormControl>

            <FormControl sx={{ mb: 2 }} fullWidth>
              <TextField
                autoComplete={'current-password'}
                error={!isValidPassword(password) && password !== ''}
                helperText={
                  !isValidPassword(password) &&
                  password !== '' &&
                  'Passwort muss mindestens 8 Zeichen lang sein, 1 Ziffer, Groß- und Kleinbuchstaben enthalten.'
                }
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position={'end'}>
                        <IconButton
                          onClick={onTogglePasswordMode}
                          onMouseDown={onDoNothing}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <VisibilityTwoTone />
                          ) : (
                            <VisibilityOffTwoTone />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                label={'Passwort'}
                onChange={onChangePassword}
                type={showPassword ? 'text' : 'password'}
                value={password}
              />
            </FormControl>

            {getStorage('errorNotConfirmed') && (
              <Button
                disabled={SEND_CONFIRMATION_DISABLED}
                onClick={onSendConfirmation}
              >
                Konto Bestätigungslink senden
              </Button>
            )}

            <Link
              to={'/pages/register-page'}
              style={{
                textTransform: 'uppercase',
                fontWeight: 'bold',
                marginLeft: '10px',
              }}
            >
              Registrieren
            </Link>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Button
              onClick={onOpenModal}
              sx={{
                background: 'none !important',
                boxShadow: 'none !important',
              }}
            >
              Passwort vergessen?
            </Button>

            <Button disabled={LOGIN_BUTTON_DISABLED} type={'submit'}>
              Anmelden
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
